<template>
  <footer class="portal-footer">
    <div class="footer-top">
      <div class="container">
        <div class="footer-grid">
          <div class="footer-brand">
            <div class="footer-logo">
              <img v-if="siteConfig.siteLogo && !logoError" :src="siteConfig.siteLogo" :alt="siteConfig.siteName" class="footer-logo-img" @error="logoError = true" />
              <span v-if="siteConfig.siteLogo && !logoError && siteConfig.siteName" class="logo-divider"></span>
              <span v-if="siteConfig.siteName" class="logo-text logo-full">{{ siteConfig.siteName }}</span>
              <template v-if="!siteConfig.siteLogo && !siteConfig.siteName">
                <span class="logo-text">ENERGY</span>
                <span class="logo-accent">CORP</span>
              </template>
            </div>
            <p class="footer-desc">{{ siteConfig.siteSlogan || $t('home.subtitle') }}</p>
          </div>

          <div class="footer-links">
            <h4>{{ $t('footer.quickLinks') }}</h4>
            <router-link to="/products">{{ $t('nav.products') }}</router-link>
            <router-link to="/solutions">{{ $t('nav.solutions') }}</router-link>
            <router-link to="/cases">{{ $t('nav.cases') }}</router-link>
            <router-link to="/news">{{ $t('nav.news') }}</router-link>
            <router-link to="/downloads">{{ $t('nav.downloads') }}</router-link>
            <router-link to="/about">{{ $t('nav.about') }}</router-link>
            <router-link to="/contact">{{ $t('nav.contact') }}</router-link>
          </div>

          <div class="footer-contact" v-if="contacts.length">
            <h4>{{ $t('footer.contactInfo') }}</h4>
            <div class="footer-contact-groups">
              <div v-for="c in contacts.slice(0, 3)" :key="c.id" class="footer-contact-group">
                <p class="contact-label">{{ c.title || c.label }}</p>
                <div class="contact-details">
                  <span v-if="c.email"><i class="el-icon-message"></i> {{ c.email }}</span>
                  <span v-if="c.phone"><i class="el-icon-phone"></i> {{ c.phone }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="footer-social" v-if="hasSocial">
            <h4>{{ $t('footer.socialMedia') }}</h4>
            <div class="social-icons">
              <a v-if="whatsappUrl" :href="whatsappUrl" class="social-icon" target="_blank" :title="$t('contact.whatsappLabel')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a v-if="telegramUrl" :href="telegramUrl" class="social-icon" target="_blank" :title="$t('contact.telegramLabel')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
              </a>
              <a v-if="linkedinUrl" :href="linkedinUrl" class="social-icon" target="_blank" :title="$t('contact.linkedinLabel')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a v-if="wechatId" class="social-icon" :title="$t('contact.wechatLabel')" @click.prevent="copyWechat" style="cursor:pointer">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1-.023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.007-.27-.027-.407-.032zm-2.563 3.099c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.97-.982z"/></svg>
              </a>
              <a v-if="qqNumber" class="social-icon" :title="$t('contact.qqLabel')" @click.prevent="copyQq" style="cursor:pointer">
                <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor"><path d="M11.136 6.162c.01-.182.01-.375.01-.557 0-3.07-1.45-6.156-5.015-6.156S1.116 2.535 1.116 5.605c0 .182.01.535.01.557L.406 7.957a26 26 0 0 0-.534 1.508c-.68 2.187-.46 3.093-.292 3.113.36.044 1.401-1.647 1.401-1.647 0 .979.504 2.256 1.594 3.179-.408.126-.907.319-1.228.556-.29.213-.253.43-.201.518.228.386 3.92.246 4.985.126 1.065.12 4.756.26 4.984-.126.052-.088.088-.305-.2-.518-.322-.237-.822-.43-1.23-.557 1.09-.922 1.594-2.2 1.594-3.178 0 0 1.041 1.69 1.401 1.647.168-.02.388-.926-.292-3.113a26 26 0 0 0-.534-1.508l-.72-1.795ZM9.173 5.53a.1.1 0 0 1-.009.096c-.109.159-1.554.943-3.033.943h-.017c-1.48 0-2.925-.784-3.034-.943a.1.1 0 0 1-.018-.055q0-.022.01-.04c.13-.287 1.43-.606 3.042-.606h.017c1.611 0 2.912.319 3.042.605M4.853 4.541c-.483.022-.896-.529-.922-1.229s.344-1.286.828-1.308c.483-.022.896.529.922 1.23.027.7-.344 1.286-.827 1.307Zm2.538 0c-.484-.022-.854-.607-.828-1.308.027-.7.44-1.25.923-1.23.483.023.853.608.827 1.309-.026.7-.439 1.251-.922 1.23ZM2.336 8.433q.32.063.639.117v2.336s1.104.222 2.21.068V8.806q.49.027.937.023h.017c1.117.013 2.474-.136 3.786-.396.097.622.151 1.386.097 2.284-.146 2.45-1.6 3.99-3.846 4.012h-.091c-2.245-.023-3.7-1.562-3.846-4.011-.054-.9 0-1.663.097-2.285"/></svg>
              </a>
              <a v-if="weiboUrl" :href="weiboUrl" class="social-icon" target="_blank" :title="$t('contact.weiboLabel')">
                <svg viewBox="0 0 16 16" width="16" height="16" fill="currentColor"><path d="M10.878 1.093a4.23 4.23 0 0 1 4.031 1.305 4.22 4.22 0 0 1 .886 4.14v.001a.612.612 0 0 1-1.166-.377 3.01 3.01 0 0 0-3.495-3.873.611.611 0 1 1-.256-1.196M3.753 9.465c.548-1.11 1.972-1.74 3.233-1.411 1.304.338 1.971 1.568 1.437 2.764-.541 1.221-2.095 1.875-3.416 1.449-1.271-.411-1.812-1.67-1.254-2.802m2.658.567c.16.066.365-.009.458-.168.088-.16.03-.34-.129-.397-.156-.062-.353.013-.446.168-.09.154-.041.333.117.397m-1.607 1.314c.413.188.963.009 1.219-.4.252-.413.12-.883-.296-1.062-.41-.172-.94.005-1.194.402-.256.4-.135.874.271 1.06"/><path d="m12.014 7.238.005.001c.919.285 1.941.974 1.939 2.188 0 2.007-2.895 4.535-7.246 4.535C3.393 13.962 0 12.352 0 9.708c0-1.385.876-2.985 2.384-4.493C4.4 3.199 6.751 2.28 7.634 3.165c.39.392.427 1.065.177 1.87-.132.405.38.182.38.182 1.63-.682 3.051-.722 3.57.02.278.397.252.951-.004 1.594-.116.293.035.34.257.407m-10.4 3.101c.172 1.738 2.46 2.936 5.109 2.674 2.647-.26 4.656-1.883 4.482-3.623-.17-1.738-2.458-2.937-5.107-2.674-2.647.263-4.656 1.883-4.484 3.623m11.681-6.484a2.06 2.06 0 0 0-1.962-.634.526.526 0 1 0 .219 1.031 1.008 1.008 0 0 1 1.17 1.296.528.528 0 0 0 1.005.325 2.06 2.06 0 0 0-.432-2.018"/></svg>
              </a>
              <a v-if="douyinUrl" :href="douyinUrl" class="social-icon" target="_blank" :title="$t('contact.douyinLabel')">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="container">
        <p>{{ siteConfig.copyright || $t('footer.copyright') }}</p>
      </div>
    </div>
  </footer>
</template>

<script>
import { getContacts } from '@/api'
export default {
  data() {
    return { contacts: [], logoError: false }
  },
  computed: {
    lang() { return this.$store.state.lang },
    siteConfig() { return this.$store.state.siteConfig },
    whatsappUrl() { const v = (this.siteConfig.whatsapp || '').replace(/[\s\-@]/g, ''); return v ? 'https://wa.me/' + v : '' },
    telegramUrl() { const v = (this.siteConfig.telegram || '').replace(/^@/, '').trim(); return v ? 'https://t.me/' + v : '' },
    linkedinUrl() { const v = (this.siteConfig.linkedin || '').trim(); if (!v) return ''; return /^https?:\/\//i.test(v) ? v : 'https://' + v },
    wechatId() { return (this.siteConfig.wechat || '').trim() },
    qqNumber() { return (this.siteConfig.qq || '').trim() },
    weiboUrl() { const v = (this.siteConfig.weibo || '').trim(); if (!v) return ''; return /^https?:\/\//i.test(v) ? v : 'https://' + v },
    douyinUrl() { const v = (this.siteConfig.douyin || '').trim(); if (!v) return ''; return /^https?:\/\//i.test(v) ? v : 'https://' + v },
    hasSocial() { return this.whatsappUrl || this.telegramUrl || this.linkedinUrl || this.wechatId || this.qqNumber || this.weiboUrl || this.douyinUrl }
  },
  watch: {
    lang() { this.loadContacts() }
  },
  mounted() { this.loadContacts() },
  methods: {
    async loadContacts() {
      try {
        const res = await getContacts(this.lang)
        this.contacts = (res.data?.code === 200 ? res.data.data : []) || []
      } catch (e) { this.contacts = [] }
    },
    copyWechat() {
      if (!this.wechatId) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.wechatId).then(() => {
          this.$message.success(this.$t('contact.wechatCopied'))
        }).catch(() => {
          this.$message.info(this.wechatId)
        })
      } else {
        const ta = document.createElement('textarea')
        ta.value = this.wechatId
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message.success(this.$t('contact.wechatCopied'))
      }
    },
    copyQq() {
      if (!this.qqNumber) return
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.qqNumber).then(() => {
          this.$message.success(this.$t('contact.qqCopied'))
        }).catch(() => {
          this.$message.info(this.qqNumber)
        })
      } else {
        const ta = document.createElement('textarea')
        ta.value = this.qqNumber
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
        this.$message.success(this.$t('contact.qqCopied'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$gold: #c9a84c;
$dark: #0a0a1a;

.portal-footer { background: #050510; }

.footer-top { padding: 60px 0 40px; }

.footer-grid {
  display: flex; flex-wrap: wrap; gap: 40px; align-items: flex-start;
  > * { flex: 1 1 0; min-width: 150px; }
}

.footer-logo {
  margin-bottom: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  .logo-text { font-size: 20px; font-weight: 700; color: #fff; letter-spacing: 3px; }
  .logo-accent { font-size: 20px; font-weight: 300; color: $gold; letter-spacing: 3px; }
  .logo-full {
    font-family: 'Montserrat', sans-serif;
    font-size: 16px; font-weight: 600; color: #fff; letter-spacing: 1.5px;
    line-height: 1.3;
  }
  .logo-divider {
    width: 1px; height: 24px; background: linear-gradient(180deg, transparent, $gold, transparent);
    flex-shrink: 0;
  }
  .footer-logo-img {
    max-height: 40px; max-width: 140px; width: auto; height: auto;
    object-fit: contain; display: block; flex-shrink: 0;
  }
}

.footer-desc { color: rgba(255,255,255,0.5); font-size: 14px; line-height: 1.8; }

.footer-contact {
  .contact-label { color: $gold; font-size: 13px; font-weight: 600; margin-bottom: 4px; letter-spacing: 1px; }
  .footer-contact-group { margin-bottom: 14px; &:last-child { margin-bottom: 0; } }
  .contact-details {
    display: flex; flex-wrap: wrap; gap: 6px 16px;
    span { color: rgba(255,255,255,0.5); font-size: 14px; white-space: nowrap; }
  }
}

.footer-links, .footer-contact, .footer-social {
  h4 { color: $gold; font-size: 14px; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 20px; font-weight: 600; }
  a, p { display: block; color: rgba(255,255,255,0.5); font-size: 14px; margin-bottom: 10px; transition: color 0.3s; }
  a:hover { color: $gold; }
}

.social-icons { display: flex; flex-wrap: wrap; gap: 12px; }
.social-icon {
  width: 36px; height: 36px; border: 1px solid rgba(255,255,255,0.2); border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; color: rgba(255,255,255,0.5); transition: all 0.3s;
  &:hover { border-color: $gold; color: $gold; }
}

.footer-bottom {
  border-top: 1px solid rgba(255,255,255,0.08); padding: 20px 0;
  p { text-align: center; color: rgba(255,255,255,0.3); font-size: 13px; }
}

@media (max-width: 576px) {
  .footer-grid { flex-direction: column; gap: 30px; }
  .footer-grid > * { min-width: 0; }
}
</style>
